import * as dayjs from "dayjs";

export const LISTING_STATUS_CODE = {
  DRAFT: 1,
  ONLINE: 2,
  OFFLINE: 3,
  DELETED: 4,
  EXPIRED: 5,
};

export const LISTING_V3_STATUS_CODE = {
  ACTIVE: "ACT",
  DRAFT: "DRAFT",
  COMPLETED: "COMP",
  REMOVED: "REM",
  EXPIRED: "EXP",
  SUSPENDED: "SUSP",
  PAUSE: "PAUSE",
};

export const LISTING_CROSS_LIST_TYPE_CODE = {
  STANDALONE: 1,
  ORIGINAL: 2,
  CROSS_LIST: 3,
};

export const PERFORMANCE_DETAILS_CODE = {
  LISTINGS: "listings",
  DISTRICTS: "districts",
  ESTATE: "estate",
  AREA: "area",
  BUILDING: "building",
  USERS: "users",
  TOWNSHIP: "township",
};

export const PERFORMANCE_CHANNEL_CODE = {
  BUY: 1,
  RENT: 2,
  BOTH: 3,
};

export const PERFORMANCE_CATEGORY_CODE = {
  residential: 1,
  commercial: 2,
};

export const TENURE_CODE = {
  freehold: 1,
  leasehold: 2,
  reservedLand: 3,
};

export const MEASUREMENT_UNIT_CODE = {
  squareFeet: 1,
  acres: 2,
};

export const DOWNLOAD_REPORT_STATUS_CODE = {
  INITIALIZED: 1,
  PROCESSING: 2,
  COMPLETED: 3,
};

export const DOWNLOAD_SPECIALIST_REPORT_STATUS_CODE = {
  INITIALIZED: 1,
  PROCESSING: 2,
  COMPLETED: 3,
};

export const PERFORMANCE_DURATION_CODE = {
  ONEWEEK: "1W",
  ONEMONTH: "1M",
  THREEMONTH: "3M",
};

export const PERFORMANCE_SORT_CODE = {
  ASCE: "ASCE",
  DESC: "DESC",
};

export const DATE_FORMAT = {
  ymd: "YYYYMMDD",
  ymd_d: "YYYY-MM-DD",
  dmy: "DDMMYYYY",
  dmy_s: "DD/MM/YYYY",
  dmy_y: "DD/MM/YY",
  time_12: "hh:mm A",
  time12: "hh:mmA",
  mdy_d: "MM-DD-YYYY",
  DMY: "D MMM YYYY",
  day_DMY: "dddd D, MMMM YYYY",
  dmy_time12: "DD MMM YY, hh:mma",
};

export const LEADS_TABS = {
  emails: 1,
  archive: 2,
  spam: 3,
};

export type TypeOmit<T, K> = Pick<T, Exclude<keyof T, K>>;

export const UNIT_MILLION = 1000000;

export const WARN_DAYS = 8;

export const INVALID_STRING = "-";

export type TIER_CODE = 1 | 2 | 3 | 4;

export const SEARCH_TIER_DEGRADE_CODE = 1000;

export const LISTING_PRODUCT: {
  [key in
    | "premium"
    | "feature"
    | "featured"
    | "standard"
    | "premiumPlus"]: TIER_CODE;
} = {
  premium: 1,
  feature: 2,
  featured: 2,
  standard: 3,
  premiumPlus: 4,
};
export const LISTING_TIERS: {
  [key in "premium" | "featured" | "standard" | "premiumPlus"]: string;
} = {
  premium: "premium",
  featured: "featured",
  standard: "standard",
  premiumPlus: "Premium Plus",
};

export const LISTING_PRODUCT_LABEL: {
  [key in number]:
    | "premiumPlus"
    | "premium"
    | "feature"
    | "featured"
    | "standard";
} = {
  1: "premium",
  2: "feature",
  3: "standard",
  4: "premiumPlus",
};

export const LISTING_VISIBILITY_LABEL: {
  [key in number]: "firstPage" | "medium" | "low";
} = {
  1: "firstPage",
  2: "medium",
  3: "low",
};

export const LISTING_VISIBILITY: {
  [key in typeof LISTING_VISIBILITY_LABEL[number]]: 1 | 2 | 3;
} = {
  firstPage: 1,
  medium: 2,
  low: 3,
};

export const STEP_NAMES = {
  listingType: "Listing Type",
  location: "Location",
  propertyDetails: "Property Details",
  facilities: "Facilities",
  description: "Description",
  media: "Media",
  upgrade: "Upgrade",
  confirmation: "Confirmation",
  "listing-details": "Listing details",
  "property-details": "Property Details",
  "description-and-media": "Description & Media ",
};

export const ANCHOR_ID = {
  overview: {
    tier: "tier",
    summary: "listing-type",
    location: "location",
    propertyDetail: "property-details",
    facilities: "facilities",
    description: "description",
    liveTour: "live-tour",
    media: "media",
    tenure: "tenure",
    tenancyRule: "tenancy-rule",
    unitDetails: "unit-details",
    additional: "additional",
  },
};

export const LISTING_CHANNEL_CODE = {
  sale: 1,
  rent: 2,
  both: 3,
};

export const PROPERTY_CATEGORY_TYPE_CODE = {
  RESIDENTIAL: 1,
  COMMERCIAL: 2,
};

export const PROPERTY_TYPE_CODE = {
  Apartment: 1,
  standBuilding: 2,
  villageHouseCode: 3,
  boatCode: 4,
  estate: 5,
  public: 6,
  carParkCode: 7,
  houseVillaCode: 8,
};

export const URL_PREFIX = process.env.REACT_APP_URL_PREFIX || "";

export const ROUTE_URL = {
  listings: URL_PREFIX + "/listings",
  overviewURL: URL_PREFIX + "/listing/overview",
  listing: {
    listingType: URL_PREFIX + "/edit-listing/listing-details/",
    location: URL_PREFIX + "/edit-listing/location/",
    propertyDetail: URL_PREFIX + "/edit-listing/property-details/",
    facilities: URL_PREFIX + "/edit-listing/property-details/",
    description: URL_PREFIX + "/edit-listing/description-and-media/",
    media: URL_PREFIX + "/edit-listing/description-and-media/",
    upgrade: URL_PREFIX + "/edit-listing/upgrade/",
    confirmation: URL_PREFIX + "/edit-listing/confirmation/",
    v3: {
      index: URL_PREFIX + "/v2/add-listing",
      unitDetails: URL_PREFIX + "/v2/add-listing/:id#/unit-details",
      gallery: URL_PREFIX + "/v2/add-listing/:id#/gallery",
    },
  },
  addListing: {
    root: URL_PREFIX + "/add-listing",
    success: URL_PREFIX + "/add-listing/success",
  },
  reporting: URL_PREFIX + "/listing/reporting",
  agencyBranch: {
    list: URL_PREFIX + "/agency-branch/list",
    create: URL_PREFIX + "/agency-branch/create",
    edit: URL_PREFIX + "/agency-branch/edit",
  },
  batchUpload: URL_PREFIX + "/listings/batch-upload",
  plans: {
    subscription: URL_PREFIX + "/plans/subscription",
    allocation: URL_PREFIX + "/plans/allocation",
    creditUsage: URL_PREFIX + "/plans/credit-usage",
  },
  enquirySettings: URL_PREFIX + "/enquiry-settings",
  reportings: {
    performance: URL_PREFIX + "/reportings/performance",
    specialist: URL_PREFIX + "/reportings/specialist",
  },
  allocations: {
    default: URL_PREFIX + "/allocations",
    individual: URL_PREFIX + "/allocations/individual",
    allocation: URL_PREFIX + "/allocations/allocation",
    report: URL_PREFIX + "/allocations/report",
  },
  quality: {
    default: URL_PREFIX + "/quality-dashboard",
    image: URL_PREFIX + "/quality-verification-image",
    address: URL_PREFIX + "/quality-verification-address",
  },
  watermark: URL_PREFIX + "/watermark",
  leads: {
    leadList: URL_PREFIX + "/leads",
    leadDetail: URL_PREFIX + "/lead",
    v2: {
      list: URL_PREFIX + "/v2/leads",
      detail: URL_PREFIX + "/v2/lead",
    },
  },
  agentools: {
    default: URL_PREFIX + "/search-properties",
  },
  propertyWanted: {
    default: URL_PREFIX + "/property-wanted",
  },
  fsbo: {
    default: URL_PREFIX + "/fsbo-alerts",
  },
  marketAnalytics: {
    landing: URL_PREFIX + "/market-analytics",
    overview: URL_PREFIX + "/market-analytics/overview",
    marketComparison: URL_PREFIX + "/market-analytics/market-comparison",
    buildingComparison: URL_PREFIX + "/market-analytics/building-comparison",
    visitTimes: URL_PREFIX + "/market-analytics/visit-times",
    tabs: URL_PREFIX + "/market-analytics/:tabName",
  },
  marketWatch: {
    landing: URL_PREFIX + "/market-watch",
  },
  dashboard: {
    default: URL_PREFIX + "/dashboard",
    announcements: URL_PREFIX + "/dashboard/announcements",
    subscription: URL_PREFIX + "/dashboard/subscription",
    allocation: URL_PREFIX + "/dashboard/allocation",
  },
  scheduledManager: {
    default: URL_PREFIX + "/scheduled-manager",
  },
  enquiryManagement: {
    root: URL_PREFIX + "/enquiry-management",
    overView: URL_PREFIX + "/enquiry-management/overview",
  },
  announcements: {
    root: URL_PREFIX + "/announcements",
    create: URL_PREFIX + "/add-announcement",
    edit: URL_PREFIX + "/edit-announcement",
    preview: URL_PREFIX + "/preview-announcement",
  },
  notifications: URL_PREFIX + "/notifications",
  areaCondoSpecialist: {
    default: URL_PREFIX + "/area-condo-specialist",
    create: URL_PREFIX + "/area-condo-specialist/add",
    createSuccess: URL_PREFIX + "/area-condo-specialist/add-success",
    upgradeListing: URL_PREFIX + "/area-condo-specialist/upgrade-listing",
    slotList: URL_PREFIX + "/area-condo-specialist/slot",
    slotEdit: URL_PREFIX + "/area-condo-specialist/slot/edit",
    slotEditSuccess: URL_PREFIX + "/area-condo-specialist/slot/edit-success",
  },
  creditBrickz: URL_PREFIX + "/credit-brickz",
  subscriptionRenewal: URL_PREFIX + "/subscription-renewal",
  paymentResult: URL_PREFIX + "/payment-result",
  paymentHistory: URL_PREFIX + "/payment-history",
  dynamicPricing: URL_PREFIX + "/dynamic-pricing",
};

export const AUPURL = {
  "zh-CN": "https://www.squarefoot.com.hk/chinese-hk/section/terms/",
  "zh-HK": "https://www.squarefoot.com.hk/chinese-hk/section/terms/",
  "en-GB": "https://www.squarefoot.com.hk/section/terms/",
};

export const LOCALSTORAGE = {
  searchParams: {
    overview: "searchParams.overview",
    allocation: {
      params: "searchParams.allocation",
      searchedOption: "allocation.searchedOption",
    },
    individual: {
      searchedOption: "individual.searchedOption",
    },
  },
};

export const QUALITY_STEPS: Array<keyof typeof STEPS> = [
  "location",
  "descriptionAndMedia",
];

export const STEPS = {
  location: ["location.unit", "location.floor", "location.carParkNo"],
  descriptionAndMedia: ["title", "images"],
};

export const FIELDS = {
  building: "building",
  district: "district",
  salePrice: "salePrice",
  rentPrice: "rentPrice",
  saleableArea: "saleableArea",
  grossArea: "grossArea",
  bedroom: "bedrooms",
  carPark: "carPark",
  images: "images",
  area: "area",
  address: "address",
  unit: "unit",
  floor: "floor",
  carParkNo: "carParkNo",
  channel: "channelCode",
  referenceNumber: "listingRefNo",
  propertyNumber: "propertyNumber",
  isPropertyNoOptional: "isPropertyNoOptional",
  propertyType: "propertyTypeCode",
  primaryAgent: "primary-agent" as "primary-agent",
  secondaryAgent: "secondary-agent" as "secondary-agent",
};

export const SORT = {
  NEW_TO_OLD: "new-to-old",
  OLD_TO_NEW: "old-to-new",
  HIGH_TO_LOW: "high-to-low",
  LOW_TO_HIGH: "low-to-high",
  NEW_TO_OLD_EXPIRY_DATE: "new-to-old-expiry-date",
  OLD_TO_NEW_EXPIRY_DATE: "old-to-new-expiry-date",
  NEW_TO_OLD_POSTED_DATE: "new-to-old-posted-date",
  OLD_TO_NEW_POSTED_DATE: "old-to-new-posted-date",
  UPGRADE_EXPIRY_DATE: "product-expiry-date",
};

export const QUALITY_FILTER = {
  complete: "true",
  inComplete: "false",
};

export const AGENT_TYPE_FILTER = {
  primary: "primary",
  secondary: "secondary",
};

export const SCHEDULE_TYPE = {
  onlyOnce: 1,
  everyDay: 2,
  weekDays: 3,
  weekends: 4,
  specificDay: 5,
};

export const ORGANIZATION = {
  agencyCode: 1,
  branchCode: 2,
};

export const USER_ROLE_CODE = {
  customer_ops: 3,
  master: 4,
  admin: 5,
  agent: 6,
  IT: 1,
  quality_manager: 8,
  super_admin: 2,
};

export const SCHEDULE_LISTING_STATUS_CODE = {
  queue: "1",
  success: "2",
  fail: "3",
  cancel: "4",
};

export const ERROR_CODE = {
  notFound: 404,
};

export const KEYBOARD = {
  enter: 13,
};

export const NEED_HANDLE_FIELD_ERRORS: { [P in string]: boolean } = {
  UNEXPIRED_LEASE_YEAR_EXCEED_MAX_LENGTH: true,
  SALE_PRICE_MANDATORY: true,
  RENT_PRICE_MANDATORY: true,
};

export const EMPTYLISTINGDISTRIBUTION = {
  listingDistribution: {
    dateRange: {
      startdate: "",
      enddate: "",
    },
    enquiries: {
      distribution: [],
    },
    filter: "",
    impressions: {
      distribution: [],
    },
    listingId: null,
    pageViews: {
      distribution: [],
    },
  },
};

export const EMPTYLISTINGPERFORMANCE = {
  listingPerformance: {
    dateRange: {
      startDate: "",
      endDate: "",
    },
    engagement: {
      socialShare: {
        link: 0,
        email: 0,
        facebook: 0,
        other: 0,
        total: 0,
        whatsapp: 0,
        googleplus: 0,
      },
      viewPhotos: 0,
      savedListing: 0,
      downloadPdf: 0,
      tour360: 0,
      viewFloorPlans: 0,
      viewVideos: 0,
    },
    enquiries: {
      feature: 0,
      email: 0,
      tier1: {
        email: 0,
        phone: 0,
        Total: 0,
        whatsapp: 0,
        name: "Standard",
      },
      tier2: {
        phone: 0,
        Total: 0,
        whatsapp: 0,
        name: "Feature",
        email: 0,
      },
      tier3: {
        email: 0,
        phone: 0,
        Total: 0,
        whatsapp: 0,
        name: "Premium",
      },
      standard: 0,
      phone: 0,
      whatsapp: 0,
      total: 0,
      premium: 0,
    },
    filter: "",
    impression: {
      comparison: 0,
      premium: 0,
      feature: 0,
      standard: 0,
      total: 0,
    },
    listingId: null,
    pageView: {
      standard: 0,
      app: 0,
      web: 0,
      mobile: 0,
      total: 0,
      premium: 0,
      feature: 0,
      tier1: {
        app: 0,
        web: 0,
        total: 0,
        mobile: 0,
        name: "Standard",
      },
      tier2: {
        app: 0,
        web: 0,
        total: 0,
        mobile: 0,
        name: "Feature",
      },
      tier3: {
        web: 0,
        mobile: 0,
        total: 0,
        name: "Premium",
        app: 0,
      },
    },
  },
};

export const EMPTYMARKETCOMPARISON = {
  marketComparison: {
    listingId: null,
    filter: "1M",
    hasBuiltUp: true,
    hasLand: true,
    areaRange: {
      builtUp: {
        low: 0,
        high: 0,
      },
      land: {
        low: 0,
        high: 0,
      },
    },
    impressions: {
      builtUp: {
        district: 0,
        estate: 0,
        self: 0,
        districtPercentage: "",
        estatePercentage: "",
      },
      land: {
        district: 0,
        estate: 0,
        self: 0,
        districtPercentage: "",
        estatePercentage: "",
      },
    },
    pageViews: {
      builtUp: {
        district: 0,
        estate: 0,
        self: 0,
        districtPercentage: "",
        estatePercentage: "",
      },
      land: {
        district: 0,
        estate: 0,
        self: 0,
        districtPercentage: "",
        estatePercentage: "",
      },
    },
  },
};

export type ISSSUE_CODE = 1 | 2;

export const QUALITY_ISSUE_TYPE_CODE: {
  [key in "image" | "address"]: ISSSUE_CODE;
} = {
  address: 1,
  image: 2,
};

export const QUALITY_ISSUE_TYPE_LABEL: {
  [key in number]: "Image" | "Address";
} = {
  1: "Address",
  2: "Image",
};

export const QUALITY_ISSUE_VERIFIED_LABEL: { [key in number]: "No" | "Yes" } = {
  0: "No",
  1: "Yes",
};

export type ISSSUE_ADDRESS_CODE = 1 | 2 | 3;

export const QUALITY_ISSUE_ADDRESS_TYPE_CODE: {
  [key in "invalid" | "valid" | "unverified"]: ISSSUE_ADDRESS_CODE;
} = {
  valid: 1,
  unverified: 2,
  invalid: 3,
};

export const QUALITY_ISSUE_ADDRESS_LABEL: {
  [key in number]: "Invalid" | "Valid" | "Unverified";
} = {
  1: "Valid",
  2: "Unverified",
  3: "Invalid",
};

export const ROI_REPORTING_LOCATION = {
  DISTRICT: "district",
  ESTATE: "estate",
};

export const ROI_REPORTING_TAB = {
  impressions: "impressions",
  pageViews: "pageViews",
  enquiries: "enquiries",
};

export const MARKET_COMPARISON_AREA = {
  builtUp: "builtUp",
  land: "land",
};

export const ROI_REPORTING_DURATION = {
  oneWeek: "1week",
  oneMonth: "1month",
  threeMonths: "3months",

  sevenDays: "7D",
  thirtyDays: "30D",
};

export const ROI_REPORTING_TYPE = {
  PRO: "pro",
  AGENTNET: "agentnet",
};

export const DEFAULT_MIN_PRICE = 0;
export const DEFAULT_MAX_PRICE = 100000000;
export const PREMIUM_MIN_PHOTO_SIZE = 5;
export const LARGE_NUMBER_SEARCH_LIMIT = 9999;

export const PREMIUM_LISTING_DAYS = 28;

export const GENERAL_LISTING_ERROR: {
  [key in string]:
    | "DESCRIPTION_EXCEED_MAX_LENGTH"
    | "DESCRIPTION_CONTAINS_RACIST_WORD";
} = {
  DESCRIPTION_EXCEED_MAX_LENGTH: "DESCRIPTION_EXCEED_MAX_LENGTH",
  DESCRIPTION_CONTAINS_RACIST_WORD: "DESCRIPTION_CONTAINS_RACIST_WORD",
};

export const COPY_LISTING_ERROR: {
  [key in string]:
    | "DESCRIPTION_EXCEED_MAX_LENGTH"
    | "DESCRIPTION_CONTAINS_RACIST_WORD";
} = {
  DESCRIPTION_EXCEED_MAX_LENGTH: "DESCRIPTION_EXCEED_MAX_LENGTH",
  DESCRIPTION_CONTAINS_RACIST_WORD: "DESCRIPTION_CONTAINS_RACIST_WORD",
};

const MINUTE_OF_DAY = 60 * 24;
const BEFORE_TIME_OFFSET_IN_MINUTE = 5;
export function getDayTimeReference(
  minuteDiff: number = 30,
  options?: { beforeTime?: dayjs.ConfigType; afterTime?: dayjs.ConfigType }
) {
  const numOfData = MINUTE_OF_DAY / minuteDiff;
  const maxMinute = options?.beforeTime
    ? dayjs(options.beforeTime).minute() +
      BEFORE_TIME_OFFSET_IN_MINUTE +
      dayjs(options.beforeTime).hour() * 60
    : MINUTE_OF_DAY + 1;
  const minMinute = options?.afterTime
    ? dayjs(options.afterTime).minute() + dayjs(options.afterTime).hour() * 60
    : -1;
  const time = [];
  for (let i = 0; i < numOfData; i++) {
    const minute = i * minuteDiff;
    if (
      (maxMinute < minMinute && (minute <= maxMinute || minMinute <= minute)) ||
      (minMinute <= minute && minute <= maxMinute)
    ) {
      time.push({
        id: minute,
        code: minute,
        description: dayjs()
          .hour(minute / 60)
          .minute(minute % 60)
          .format("h:mmA"),
      });
    }
  }
  return time;
}

export type SELF_SERVE_PRODUCT_CODE = 1 | 2 | 3 | 4 | 5;

export const SELF_SERVE_PRODUCT_TYPE: {
  [key in
    | "subscription"
    | "listingCredit"
    | "featuredCredit"
    | "adCredit"
    | "brickz"]: SELF_SERVE_PRODUCT_CODE;
} = {
  subscription: 1,
  listingCredit: 2,
  featuredCredit: 3,
  adCredit: 4,
  brickz: 5,
};

export type SELF_SERVE_CATEGORY_CODE = 1 | 2 | 3 | 4;

export const SELF_SERVE_CATEGORY_TYPE: {
  [key in
    | "creditPackages"
    | "brickz"
    | "subscription"
    | "pg"]: SELF_SERVE_CATEGORY_CODE;
} = {
  creditPackages: 1,
  brickz: 2,
  subscription: 3,
  pg: 4,
};

export type SUBSCRIPTION_TIER_CODE = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const SUBSCRIPTION_TIER: {
  [key in
    | "iClassic"
    | "iPlatinum"
    | "iGold"
    | "iBasic"
    | "iMax"
    | "iPro"
    | "iCore"]: SUBSCRIPTION_TIER_CODE;
} = {
  iClassic: 1,
  iPlatinum: 2,
  iGold: 3,
  iBasic: 4,
  iMax: 5,
  iPro: 6,
  iCore: 7,
};

export type SELF_SERVE_RELATED_PRODUCT_CATEGORY_CODE = 1 | 2 | 3 | 4;

export const SELF_SERVE_RELATED_PRODUCT_CATEGORY: {
  [key in
    | "listingProducts"
    | "analyticProducts"
    | "otherProducts"
    | "pgProducts"]: SELF_SERVE_RELATED_PRODUCT_CATEGORY_CODE;
} = {
  listingProducts: 1,
  otherProducts: 2,
  analyticProducts: 3,
  pgProducts: 4,
};

export type SELF_SERVE_RELATED_PRODUCT_TYPE_CODE = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const SELF_SERVE_RELATED_PRODUCT_TYPE: {
  [key in
    | "featuredCredits"
    | "adCredits"
    | "listingCredits"
    | "subscription"
    | "brickz"
    | "pgConcurrentListing"
    | "pgAdCredit"]: SELF_SERVE_RELATED_PRODUCT_TYPE_CODE;
} = {
  featuredCredits: 1,
  adCredits: 2,
  listingCredits: 3,
  subscription: 4,
  brickz: 5,
  pgConcurrentListing: 6,
  pgAdCredit: 7,
};

export const PRICING_SHEET_STATUS = {
  draft: 1,
  active: 2,
  scheduled: 3,
  past: 4,
};

export type PAYMENT_STATUS_CODE = 1 | 2;
export const PAYMENT_STATUS: {
  [key in "Successful" | "Unsuccessful"]: PAYMENT_STATUS_CODE;
} = {
  Successful: 1,
  Unsuccessful: 2,
};

export enum LocationLevelType {
  Level1 = "level1",
  Level2 = "level2",
  Level3 = "level3",
  Level4 = "level4",
  Level5 = "level5",
}

export enum InventorySummaryType {
  SUBSCRIPTION = 1,
  ADDON = 2,
  CARRY_OVER = 3,
  EXTENDED_PACKAGE = 4,
  CREDIT_CONVERSION = 5,
}

export const INVENTORY_SUMMARY_TYPE_LABEL: {
  [key in InventorySummaryType]: string;
} = {
  [InventorySummaryType.SUBSCRIPTION]: "Subscription package",
  [InventorySummaryType.ADDON]: "Add-on",
  [InventorySummaryType.CARRY_OVER]: "Carry over",
  [InventorySummaryType.EXTENDED_PACKAGE]: "Extended package",
  [InventorySummaryType.CREDIT_CONVERSION]: "Credit conversion: 01/04/2026",
};

type ChannelSetting = {
  minPsf: number;
};

type Settings = {
  [key in number]: ChannelSetting;
};
export const MIN_PSF_PRICE_VALIDATION: Settings = {
  [LISTING_CHANNEL_CODE.sale]: {
    minPsf: 0.1,
  },
  [LISTING_CHANNEL_CODE.rent]: {
    minPsf: 0.1,
  },
};

export const CHANNEL_CODE_AUCTION = 100;

export const QUOTE_TYPES = {
  universalCredit: 4,
};
